import { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { SendIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Column, Row } from "@/components";
import {
  CommunicationsScopeBanner,
  type CommunicationsTab,
} from "@/components/user-sheet/comms/CommunicationsScopeBanner";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";

import { cn } from "@/lib/utils";

const sendMessageSchema = z.object({
  body: z.string().trim().min(1),
});

export type SendMessageValues = z.infer<typeof sendMessageSchema>;

export const MessageComposer = ({
  autoFocus = true,
  candidateName,
  communicationsTab = "external",
  companyName,
  isReply = false,
  messageDraft = "",
  onMessageDraftChange,
  onSend,
  phone,
  placeholder = "Type a message...",
}: {
  autoFocus?: boolean;
  candidateName: string;
  communicationsTab?: CommunicationsTab;
  companyName?: string;
  isReply?: boolean;
  messageDraft?: string;
  onMessageDraftChange?: (body: string) => void;
  onSend: (values: SendMessageValues) => void;
  phone?: string | null;
  placeholder?: string;
}) => {
  /* React Hooks */
  const form = useForm<SendMessageValues>({
    resolver: zodResolver(sendMessageSchema),
    defaultValues: { body: messageDraft },
  });
  const bodyValue = form.watch("body");
  const isPhoneGated = phone !== undefined;
  const canSend = (!isPhoneGated || !!phone) && bodyValue.trim().length > 0;

  useEffect(() => {
    if (autoFocus && (!isPhoneGated || phone)) {
      form.setFocus("body");
    }
  }, [autoFocus, form, isPhoneGated, phone]);

  /* Handlers */
  const handleSubmit = (values: SendMessageValues) => {
    if (isPhoneGated && !phone) return;

    form.reset({ body: "" });
    if (onMessageDraftChange) {
      onMessageDraftChange("");
    }
    requestAnimationFrame(() => {
      form.setFocus("body");
    });
    onSend(values);
  };

  /* Render */
  const renderSendButton = ({ className }: { className?: string }) => {
    return (
      <Button
        className={cn("shrink-0", className)}
        disabled={!canSend}
        onMouseDown={(event) => {
          event.preventDefault();
        }}
        size="icon"
        type="submit"
        variant={isReply ? "ghost" : "default"}
      >
        <SendIcon className="h-4 w-4" />
      </Button>
    );
  };

  const render = () => {
    return (
      <Column className={cn("w-full gap-1.5", isReply ? "pt-1.5" : "pt-3")}>
        <Form {...form}>
          <form
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={form.handleSubmit(handleSubmit)}
            className="flex flex-col gap-3"
          >
            <FormField
              control={form.control}
              name="body"
              render={({ field }) => (
                <FormItem className="space-y-0">
                  <FormControl>
                    {isReply ? (
                      <div className="relative">
                        <Textarea
                          {...field}
                          autoExpand
                          className="max-h-32 min-h-10 resize-none border-slate-300/70 bg-primary-50 py-2.5 pr-11 focus-visible:ring-primary-300"
                          disabled={isPhoneGated && !phone}
                          onChange={(event) => {
                            field.onChange(event);
                            if (onMessageDraftChange) {
                              onMessageDraftChange(event.target.value);
                            }
                          }}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" && !event.shiftKey) {
                              event.preventDefault();
                              event.currentTarget.form?.requestSubmit();
                            }
                          }}
                          placeholder={
                            isPhoneGated && !phone
                              ? "No phone number"
                              : placeholder
                          }
                          rows={1}
                        />
                        {renderSendButton({
                          className:
                            "absolute bottom-1 right-1 h-8 w-8 text-primary-900 hover:bg-primary-100 hover:text-primary-900",
                        })}
                      </div>
                    ) : (
                      <Textarea
                        {...field}
                        autoExpand
                        className="max-h-32 min-h-10 resize-none border-slate-300/70 bg-primary-50 focus-visible:ring-primary-300"
                        disabled={isPhoneGated && !phone}
                        onChange={(event) => {
                          field.onChange(event);
                          if (onMessageDraftChange) {
                            onMessageDraftChange(event.target.value);
                          }
                        }}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" && !event.shiftKey) {
                            event.preventDefault();
                            event.currentTarget.form?.requestSubmit();
                          }
                        }}
                        placeholder={
                          isPhoneGated && !phone
                            ? "No phone number"
                            : placeholder
                        }
                        rows={1}
                      />
                    )}
                  </FormControl>
                </FormItem>
              )}
            />
            {isReply ? null : (
              <Row className="items-center gap-2">
                <CommunicationsScopeBanner
                  candidateName={candidateName}
                  className="min-w-0 flex-1"
                  communicationsTab={communicationsTab}
                  companyName={companyName}
                />
                {renderSendButton({ className: "h-9 w-9 rounded-full" })}
              </Row>
            )}
          </form>
        </Form>
      </Column>
    );
  };

  return render();
};
